<script lang="ts" setup generic="T extends string | number">
import WbIcon from '../../../wb/Icon/WbIcon.vue';

type TransmissionMode = 'manual' | 'automatic';

const props = defineProps<{
  name: string;
  label: string;
  options: any;
  selected: T | TransmissionMode | undefined | null;
  available: T[];
  getOptionLabel: (value: T) => string | number;
  onSelect: (value: T) => void;
  onTrack?: (key: string, vehicle: any) => void;
  trackPrefix?: string;
  vehicle: any;
}>();

const isAvailable = (option: T) => props.available.includes(option);
const isSelected = (option: T) => option === props.selected;

const handleSelect = (option: T, event: Event) => {
  if (!isAvailable(option)) {
    event.preventDefault();
    return;
  }

  props.onSelect(option);
  props.onTrack?.(
    `${props.trackPrefix ?? props.name}.${option}`,
    props.vehicle,
  );
};
</script>

<template>
  <fieldset class="owvans-tech-data-tag-group">
    <legend class="owvans-tech-data-tag-group__legend wb-text-s">
      {{ label }}
    </legend>
    <div class="owvans-tech-data-tag-group__options">
      <label
        v-for="(option, index) in options"
        :key="`${name}-${option}`"
        class="wbx-tag wb-text-s-strong wbx-tag--tertiary owvans-tech-data-tag-group__option"
        :class="{
          'owvans-tech-data-tag-group__option--default': !isSelected(option),
          'owvans-tech-data-tag-group__option--active': isSelected(option),
          'owvans-tech-data-tag-group__option--disabled': !isAvailable(option),
        }"
      >
        <input
          class="owvans-tech-data-tag-group__input"
          type="radio"
          :name="name"
          :value="String(option)"
          :tabindex="
            isSelected(option) || (!props.selected && index === 0) ? 0 : -1
          "
          :checked="isSelected(option) || (!props.selected && index === 0)"
          :disabled="!isAvailable(option)"
          @change="handleSelect(option, $event)"
        />
        <span class="wb-text wb-text-m-strong-static">
          {{ getOptionLabel(option) }}
        </span>
        <WbIcon
          :name="
            isSelected(option)
              ? 'bds/hotspot-active/24'
              : 'bds/hotspot-default/24'
          "
          size="M"
        />
      </label>
    </div>
  </fieldset>
</template>

<style scoped lang="scss">
.owvans-tech-data-tag-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  border: 0;
  min-inline-size: 0;

  &__legend {
    color: var(--wb-grey-20);
    font-size: 1rem;
    font-weight: 700;
    margin-bottom: 8px;
  }

  &__options {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    min-inline-size: 0;
  }

  &__option {
    display: flex;
    flex: 0 0 auto;
    position: relative;
    align-items: center;
    gap: var(--wb-interactive-tag-md-gap-right);
    border: none;
    border-radius: var(--wb-interactive-tag-border-radius);
    padding: 4px 11px;
    cursor: pointer;
  }

  &__option--default {
    background-color: transparent;
    border: 1px solid var(--wb-grey-20);

    &:hover {
      box-shadow: 0 0 0 1px
        var(--wb-interactive-tag-tertiary-unfilled-border-main-color-hover);
    }
  }

  &__option--active {
    &:hover {
      background-color: #d5d5d5;
    }
  }

  &__option--disabled {
    opacity: 0.4;
    cursor: not-allowed;

    &:hover {
      background-color: transparent;
      box-shadow: none;
    }
  }

  &__input {
    position: absolute;
    inset: 0;
    opacity: 0;
    margin: 0;
    width: 100%;
    height: 100%;
    cursor: inherit;
  }

  &__option:has(.owvans-tech-data-tag-group__input:focus) {
    outline: 1px solid var(--wb-color-border-brand-active);
    outline-offset: 2px;
    border-radius: var(--wb-interactive-tag-border-radius);
  }
}
</style>
